import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Printer } from "lucide-react";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
import { TICKET_STATUS_LABEL_KEY, labelKeyFor } from "@/i18n/racketLabMaps";

/**
 * RLAdminTicketQrPrint — SS-7 (Jun 2026).
 *
 * Printable card for the canonical ticket QR (admin-only, preview-first).
 * Reads `GET /admin/racket-lab/tickets/{id}/qr-canonical` and renders a
 * compact card: QR + ticket id + status + customer reference + product /
 * brand / model + strings / tensions + scheduled date + shop / operator.
 *
 * `window.print()` with a print-isolation style block so only the card
 * is printed (admin chrome hidden). NO public scan: the QR encodes an
 * internal `teamos://` URI.
 */
const Row = ({ label, value, testId }) => (
  <div data-testid={testId} className="flex items-baseline justify-between gap-3 border-b border-dashed border-slate-200 py-1 text-xs">
    <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    <span className="text-right font-medium text-slate-800">{value ?? "—"}</span>
  </div>
);

const RLAdminTicketQrPrint = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, loading } = useAdminApi(
    `/admin/racket-lab/tickets/${encodeURIComponent(id)}/qr-canonical`,
    { skip: !id },
  );

  const p = data?.ticket_preview;
  const equipment = p
    ? [p.equipment_brand_label, p.equipment_model_label].filter(Boolean).join(" ") || p.equipment_reference_label
    : null;
  const strings = p && (p.string_main_label || p.string_cross_label)
    ? `${p.string_main_label || "—"} / ${p.string_cross_label || "—"}`
    : null;
  const tension = p && (p.tension_main != null || p.tension_cross != null)
    ? `${p.tension_main ?? "—"} / ${p.tension_cross ?? "—"} ${p.tension_unit || "kg"}`
    : null;

  return (
    <div data-testid="rl-qr-print-page" className="flex flex-col items-start gap-4">
      <style>{`@media print {
        body * { visibility: hidden; }
        #rl-qr-print-card, #rl-qr-print-card * { visibility: visible; }
        #rl-qr-print-card { position: absolute; left: 0; top: 0; box-shadow: none !important; }
      }`}</style>

      <div className="flex items-center gap-2 print:hidden">
        <button
          type="button"
          data-testid="rl-qr-print-back"
          onClick={() => navigate(`/admin/racket-lab/tickets/${id}`)}
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("tickets.qr_canonical.print.back")}
        </button>
        <button
          type="button"
          data-testid="rl-qr-print-btn"
          onClick={() => window.print()}
          disabled={!data?.exists}
          className="inline-flex items-center gap-1 rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Printer className="h-3.5 w-3.5" />
          {t("tickets.qr_canonical.print.print_btn")}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">{t("tickets.qr_canonical.loading")}</p>
      ) : !data?.exists ? (
        <p data-testid="rl-qr-print-not-found" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          {t("tickets.qr_canonical.print.not_found")}
        </p>
      ) : (
        <div
          id="rl-qr-print-card"
          data-testid="rl-qr-print-card"
          className="w-[340px] rounded-lg border border-slate-300 bg-white p-5 shadow-md"
        >
          <h1 className="mb-1 text-sm font-semibold text-slate-900">
            {t("tickets.qr_canonical.print.title")}
          </h1>
          <p className="mb-3 font-mono text-[10px] text-slate-500">{p?.id}</p>
          <div className="mb-3 flex justify-center">
            <QRCodeSVG value={data.qr_payload} size={200} data-testid="rl-qr-print-svg" />
          </div>
          <Row label={t("tickets.qr_canonical.print.status_label")} value={t(labelKeyFor(TICKET_STATUS_LABEL_KEY, p?.status))} testId="rl-qr-print-status" />
          <Row label={t("tickets.qr_canonical.print.customer_label")} value={p?.customer_reference_label} testId="rl-qr-print-customer" />
          <Row label={t("tickets.qr_canonical.print.equipment_label")} value={equipment} testId="rl-qr-print-equipment" />
          {strings ? <Row label={t("tickets.qr_canonical.print.strings_label")} value={strings} testId="rl-qr-print-strings" /> : null}
          {tension ? <Row label={t("tickets.qr_canonical.print.tension_label")} value={tension} testId="rl-qr-print-tension" /> : null}
          <Row label={t("tickets.qr_canonical.print.scheduled_label")} value={p?.scheduled_for} testId="rl-qr-print-scheduled" />
          <Row label={t("tickets.qr_canonical.print.shop_label")} value={p?.racket_lab_shop_id?.slice(0, 18)} testId="rl-qr-print-shop" />
          {p?.racket_lab_operator_id ? (
            <Row label={t("tickets.qr_canonical.print.operator_label")} value={p.racket_lab_operator_id.slice(0, 18)} testId="rl-qr-print-operator" />
          ) : null}
          {p?.photos_count > 0 ? (
            <Row label={t("tickets.photos.print_label")} value={t("tickets.photos.print_count", { count: p.photos_count })} testId="rl-qr-print-photos" />
          ) : null}
          <p className="mt-3 text-center text-[9px] text-slate-400">
            {t("tickets.qr_canonical.subtitle")}
          </p>
        </div>
      )}
    </div>
  );
};

export default RLAdminTicketQrPrint;
